import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Coins, Target, ListChecks, Lightbulb } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import HomeSkeleton from '@/components/dashboard/HomeSkeleton';
import Confetti from '@/components/Confetti';
import ShareSheet from '@/components/ShareSheet';
import HeroSection from '@/components/HeroSection';
import EmotionalProgressCard from '@/components/EmotionalProgressCard';
import DownloadAppBanner from '@/components/DownloadAppBanner';
import GrowthCards from '@/components/GrowthCards';
import AIObservations from '@/components/AIObservations';
import RankProgress from '@/components/RankProgress';
import WeeklyChallengeCard from '@/components/WeeklyChallengeCard';
import FutureMeBannerCTA from '@/components/FutureMeBannerCTA';
import MissionReflectionModal from '@/components/MissionReflectionModal';
import TodaysMissionCard from '@/components/dashboard/TodaysMissionCard';
import WeeklySummaryCard from '@/components/dashboard/WeeklySummaryCard';
import RoadmapParkLot from '@/components/dashboard/RoadmapParkLot';
import CompleteMissionModal from '@/components/dashboard/CompleteMissionModal';
import CelebrationModal from '@/components/dashboard/CelebrationModal';
import AddProgressModal from '@/components/dashboard/AddProgressModal';
import RefineMissionModal from '@/components/dashboard/RefineMissionModal';
import FailMissionModal from '@/components/dashboard/FailMissionModal';
import InsightsModal from '@/components/dashboard/InsightsModal';

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();

  // Mission state
  const [activeItems, setActiveItems] = useState([]);
  const [selectedGoalId, setSelectedGoalId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unreadNotif, setUnreadNotif] = useState(0);
  const [journey, setJourney] = useState(null);
  const [weekly, setWeekly] = useState(null);

  // Complete modal
  const [showComplete, setShowComplete] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [notes, setNotes] = useState('');
  const [proofPath, setProofPath] = useState(null);
  const [completePrompts, setCompletePrompts] = useState([]);
  const [completeAnswers, setCompleteAnswers] = useState({});
  const [promptsLoading, setPromptsLoading] = useState(false);

  // Celebration / Reflection / Share
  const [showCelebration, setShowCelebration] = useState(null);
  const [reflectionMission, setReflectionMission] = useState(null);
  const [sharePayload, setSharePayload] = useState(null);

  // Refine
  const [showRefine, setShowRefine] = useState(false);
  const [refineDetails, setRefineDetails] = useState('');
  const [refining, setRefining] = useState(false);

  // Fail
  const [showFail, setShowFail] = useState(false);
  const [failReason, setFailReason] = useState('');
  const [failNotes, setFailNotes] = useState('');
  const [failing, setFailing] = useState(false);

  // Add Progress
  const [showAddProgress, setShowAddProgress] = useState(false);
  const [progressNote, setProgressNote] = useState('');
  const [savingProgress, setSavingProgress] = useState(false);

  // Insights
  const [showInsights, setShowInsights] = useState(false);
  const [insights, setInsights] = useState(null);

  // Derive current view and mission/goal from selectedGoalId + activeItems.
  // No mirrored state → no derived-state effect → no set-state-in-effect lint.
  const current = useMemo(
    () => activeItems.find((i) => i.goal.id === selectedGoalId) || activeItems[0],
    [activeItems, selectedGoalId],
  );
  const mission = current?.mission || null;
  const goal = current?.goal || null;

  const load = useCallback(async () => {
    // iter-85: parallel fetch of all 4 home endpoints. Previously these ran
    // sequentially (await/await/await), causing the dashboard to re-render
    // 3-4 times as each promise resolved. Promise.allSettled lets us update
    // state ONCE per logical phase (critical first → secondary background).
    // Skeleton stays up until both /missions/active AND /journey resolve.
    try {
      const [missionsRes, journeyRes] = await Promise.allSettled([
        api.get('/missions/active'),
        api.get('/users/me/journey'),
      ]);
      if (missionsRes.status === 'fulfilled') {
        const items = Array.isArray(missionsRes.value.data) ? missionsRes.value.data : [];
        setActiveItems(items);
        setSelectedGoalId((cur) =>
          (cur && items.find((i) => i.goal.id === cur)) ? cur : items[0]?.goal?.id || null
        );
      }
      if (journeyRes.status === 'fulfilled') {
        setJourney(journeyRes.value.data);
      }
    } finally {
      // Hide the skeleton as soon as critical data lands — even before
      // secondary endpoints resolve. The page is already usable by then.
      setLoading(false);
    }

    // Secondary fetches don't gate the skeleton — they fill in number badges
    // and the weekly card after first paint.
    // iter-103 Phase 3: use the cheap /notifications/unread-count endpoint
    // for the bell badge instead of pulling the full 50-row list.
    Promise.allSettled([
      api.get('/notifications/unread-count'),
      api.get('/missions/weekly-summary'),
    ]).then(([notifRes, weeklyRes]) => {
      if (notifRes.status === 'fulfilled') {
        const n = Number(notifRes.value?.data?.unread || 0);
        setUnreadNotif(Number.isFinite(n) ? n : 0);
      }
      if (weeklyRes.status === 'fulfilled') {
        setWeekly(weeklyRes.value.data);
      }
    });
  }, []);

  const loadJourney = useCallback(async () => {
    // Used by post-action refreshes (e.g. mission complete, primary pin).
    try {
      const { data: j } = await api.get('/users/me/journey');
      setJourney(j);
    } catch (e) {
      if (e) console.warn('journey load failed', e?.message);
    }
  }, []);

  useEffect(() => {
    // iter-85: single combined fetch on mount. `load` already fetches journey
    // in parallel — no separate loadJourney call needed at mount.
    load();
  }, [load]);

  const switchGoal = async (goalId) => {
    setSelectedGoalId(goalId);
    try { await api.post(`/goals/${goalId}/primary`); } catch (e) { if (e) console.warn(e); }
  };

  const handleUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append('file', f);
    try {
      const { data: res } = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setProofPath(res.path);
      toast.success('Photo uploaded');
    } catch {
      toast.error('Upload failed');
    }
  };

  const openCompleteModal = async (missionArg) => {
    const m = missionArg || mission;
    if (!m) return;
    // Switch primary goal so all subsequent handlers use this mission's context.
    const it = activeItems.find((i) => i.mission?.id === m.id);
    if (it) setSelectedGoalId(it.goal.id);
    setShowComplete(true);
    setCompletePrompts([]);
    setCompleteAnswers({});
    setPromptsLoading(true);
    try {
      const { data: res } = await api.get(`/missions/${m.id}/complete-prompts`);
      setCompletePrompts(res.prompts || []);
    } catch { /* modal still works without prompts */ }
    finally { setPromptsLoading(false); }
  };

  // Per-card adapter — sets the active goal then opens the modal/handler.
  // Modals that submit via existing handlers (refine/fail/progress) read
  // `mission` from the re-derived `current` after the goal switch, which is
  // fine because submission happens on a later render.
  const focusGoal = (goalId) => setSelectedGoalId(goalId);

  const submitComplete = async () => {
    if (!mission) return;
    setCompleting(true);
    try {
      const completedMissionId = mission.id;
      const { data: res } = await api.post(`/missions/${mission.id}/complete`, {
        notes: notes || null,
        proof_path: proofPath,
        outcomes: Object.keys(completeAnswers).length ? completeAnswers : null,
      });
      setShowComplete(false);
      setNotes('');
      setProofPath(null);
      setCompletePrompts([]);
      setCompleteAnswers({});
      setShowCelebration({ ...res, completed_mission_id: completedMissionId });
      await refresh();
    } catch {
      toast.error('Failed to complete');
    } finally {
      setCompleting(false);
    }
  };

  const closeCelebration = () => {
    const completedId = showCelebration?.completed_mission_id;
    setShowCelebration(null);
    if (completedId) setReflectionMission(completedId);
    load();
    loadJourney();
  };

  const openCelebrationShare = async () => {
    if (!showCelebration) return;
    try {
      const missionId = showCelebration?.completed_mission_id;
      if (Array.isArray(showCelebration.new_achievements) && showCelebration.new_achievements.length) {
        const ach = showCelebration.new_achievements[0];
        const { data: payload } = await api.get(`/social/share/milestone/achievement?id=${ach.id}`);
        setSharePayload(payload);
        return;
      }
      if (showCelebration.level_up) {
        const { data: payload } = await api.get(`/social/share/milestone/level?level=${showCelebration.new_level}`);
        setSharePayload(payload);
        return;
      }
      if (missionId) {
        const { data: payload } = await api.get(`/social/share/milestone/mission?id=${missionId}`);
        setSharePayload(payload);
        return;
      }
      const { data: payload } = await api.get('/social/share/me/profile');
      setSharePayload(payload);
    } catch { toast.error('Could not open share'); }
  };

  const submitProgressNote = async () => {
    if (!mission || progressNote.trim().length < 2) {
      toast.error('Type a short note about your progress');
      return;
    }
    setSavingProgress(true);
    try {
      await api.post(`/missions/${mission.id}/progress-note`, { text: progressNote.trim() });
      toast.success('Progress noted — keep going!');
      setShowAddProgress(false);
      setProgressNote('');
      await load();
    } catch (e) {
      console.warn('progress note failed', e?.response?.status);
      toast.error('Could not save note');
    } finally {
      setSavingProgress(false);
    }
  };

  const submitRefine = async () => {
    if (!mission || refineDetails.trim().length < 3) {
      toast.error('Please share a bit more detail');
      return;
    }
    setRefining(true);
    try {
      const missionId = mission.id;
      const { data: refined } = await api.post(`/missions/${missionId}/refine`, {
        details: refineDetails.trim(),
      });
      // Update the corresponding entry in activeItems so `current`/`data` re-derive.
      setActiveItems((items) =>
        items.map((it) => (it.mission?.id === missionId ? { ...it, mission: refined } : it)),
      );
      setShowRefine(false);
      setRefineDetails('');
      toast.success('Mission updated to fit you better');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not refine');
    } finally {
      setRefining(false);
    }
  };

  const submitFail = async () => {
    if (!mission || failReason.trim().length < 2) {
      toast.error('Tell us why so we can adapt');
      return;
    }
    setFailing(true);
    try {
      const { data: res } = await api.post(`/missions/${mission.id}/fail`, {
        reason: failReason.trim(),
        notes: failNotes.trim() || null,
      });
      setShowFail(false);
      setFailReason('');
      setFailNotes('');
      setInsights(res.insights);
      setShowInsights(true);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save');
    } finally {
      setFailing(false);
    }
  };

  const openInsights = async () => {
    try {
      const { data: res } = await api.get('/missions/insights');
      setInsights(res);
      setShowInsights(true);
    } catch (e) {
      if (e) console.warn(e);
      toast.error('Could not load insights');
    }
  };

  const closeInsights = () => {
    setShowInsights(false);
    load();
  };

  if (!user) return null;
  if (loading) return <HomeSkeleton />;

  // Empty state — user hasn't created any goal yet.
  if (!mission || !goal) {
    return (
      <div className="p-5 gm-slide-up pb-28">
        <DownloadAppBanner />
        <HeroSection
          journey={journey}
          displayName={user?.display_name || user?.username}
          onBellClick={() => navigate('/notifications')}
          unreadCount={unreadNotif}
        />
        {journey?.journey_lines?.length > 0 && (
          <div className="mt-3">
            <EmotionalProgressCard lines={journey.journey_lines} />
          </div>
        )}
        <RankProgress rank={journey?.rank} />
        <WeeklyChallengeCard onOpen={() => navigate('/challenges')} />
        <GrowthCards cards={journey?.growth_cards} />
        <AIObservations items={journey?.ai_observations} />
        <div className="gm-card p-6 text-center mt-5">
          <Target size={48} className="mx-auto text-[#1CB0F6]" strokeWidth={3} />
          <h2 className="text-xl font-extrabold mt-3">Set your first goal</h2>
          <p className="text-[#AFAFAF] font-bold mt-2 text-sm">Tell us what you want to achieve and your AI mentor takes it from there.</p>
          <button onClick={() => navigate('/onboarding')} className="gm-btn gm-btn-primary mt-4 px-8 py-3 mx-auto" data-testid="start-onboarding-cta">
            Start
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-5 gm-slide-up pb-28">
      {showCelebration && <Confetti />}

      <DownloadAppBanner />

      <HeroSection
        journey={journey}
        displayName={user?.display_name || user?.username}
        onBellClick={() => navigate('/notifications')}
        unreadCount={unreadNotif}
        onPrimaryChanged={loadJourney}
      />

      {/* Quick actions strip */}
      <div className="flex items-center gap-2 mt-4 mb-3">
        <button onClick={openInsights} className="gm-btn gm-btn-secondary text-xs py-2 px-3" data-testid="open-insights">
          <Lightbulb size={14} strokeWidth={3} /> Insights
        </button>
        <button onClick={() => navigate('/goals')} className="gm-btn gm-btn-secondary text-xs py-2 px-3" data-testid="open-goals">
          <ListChecks size={14} strokeWidth={3} /> My goals
        </button>
        <span className="ml-auto inline-flex items-center gap-1 text-xs font-extrabold text-[#94A3B8]">
          <Coins size={14} color="#FFC800" strokeWidth={3} /> {user.coins || 0}
        </span>
      </div>

      {/* Stacked mission cards — one card per active goal with distinct shades.
          iter-88: 2-col on lg+ so the wide tablet/desktop canvas isn't wasted. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start" data-testid="mission-stack">
        {activeItems.map((item, idx) => (
          <TodaysMissionCard
            key={item.goal.id}
            mission={item.mission}
            goal={item.goal}
            index={idx}
            onComplete={() => openCompleteModal(item.mission)}
            onAddProgress={() => { focusGoal(item.goal.id); setShowAddProgress(true); }}
            onNeedHelp={() => { focusGoal(item.goal.id); navigate('/coach'); }}
            onAddDetails={() => { focusGoal(item.goal.id); setShowRefine(true); }}
            onSkip={() => { focusGoal(item.goal.id); setShowFail(true); }}
          />
        ))}
      </div>

      {/* Future Me flagship CTA — opens the Future Me hub. */}
      <FutureMeBannerCTA />

      {/* Weekly challenge / contest */}
      <div className="mt-4">
        <WeeklyChallengeCard onOpen={() => navigate('/challenges')} />
      </div>

      {/* iter-76: Park Lot positioned right after weekly challenge per user request */}
      <RoadmapParkLot />

      {/* V2 sections — pushed below today's mission per user feedback */}
      {journey?.journey_lines?.length > 0 && (
        <div className="mt-5">
          <EmotionalProgressCard lines={journey.journey_lines} />
        </div>
      )}
      <RankProgress rank={journey?.rank} />
      <GrowthCards cards={journey?.growth_cards} />
      <AIObservations items={journey?.ai_observations} />
      <WeeklySummaryCard weekly={weekly} />

      {/* Modals */}
      <CompleteMissionModal
        open={showComplete}
        prompts={completePrompts}
        promptsLoading={promptsLoading}
        answers={completeAnswers}
        setAnswers={setCompleteAnswers}
        notes={notes}
        setNotes={setNotes}
        proofPath={proofPath}
        onUpload={handleUpload}
        completing={completing}
        onClose={() => setShowComplete(false)}
        onSubmit={submitComplete}
      />

      <CelebrationModal
        celebration={showCelebration}
        onShare={openCelebrationShare}
        onClose={closeCelebration}
      />

      <ShareSheet open={!!sharePayload} onClose={() => setSharePayload(null)} payload={sharePayload} />

      <AddProgressModal
        open={showAddProgress}
        value={progressNote}
        onChange={setProgressNote}
        saving={savingProgress}
        onClose={() => setShowAddProgress(false)}
        onSubmit={submitProgressNote}
      />

      {reflectionMission && (
        <MissionReflectionModal
          missionId={reflectionMission}
          onClose={() => setReflectionMission(null)}
          onSaved={() => setReflectionMission(null)}
        />
      )}

      <RefineMissionModal
        open={showRefine}
        value={refineDetails}
        onChange={setRefineDetails}
        refining={refining}
        onClose={() => setShowRefine(false)}
        onSubmit={submitRefine}
      />

      <FailMissionModal
        open={showFail}
        reason={failReason}
        setReason={setFailReason}
        notes={failNotes}
        setNotes={setFailNotes}
        failing={failing}
        onClose={() => setShowFail(false)}
        onSubmit={submitFail}
      />

      {showInsights && <InsightsModal insights={insights} onClose={closeInsights} />}
    </div>
  );
}
